import { MouseEvent, useCallback, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Collapse,
  List,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Delete } from '@material-ui/icons';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';

import InfiniteScroll from 'components/InfiniteScroll';
import { useAppDispatch, useAppSelector } from 'store';
import {
  agendamentosSelector,
  cancelarAgendamentosEmLote,
  resetIdsAgendamentosSelecionados,
  setIdsAgendamentosSelecionados,
} from 'store/reducers/agendamentos';

import AgendamentoListItem from './AgendamentoListItem';

type ListaAgendamentosProps = {
  hasFiltersApplied: boolean;
  hasMore: boolean;
  onLoadNext: VoidFunction;
  onSuccess?: VoidFunction;
};

const useStyles = makeStyles(({ typography, spacing }) => ({
  list: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
    paddingTop: 0,
    width: '100%',
  },
  paper: {
    '& .MuiDialogTitle-root .MuiTypography-h6': {
      fontSize: typography.pxToRem(14),
      fontWeight: typography.fontWeightMedium as number,
    },
    '& .MuiDialogContent-root .MuiDialogContentText-root': {
      fontSize: typography.pxToRem(14),
    },
  },
  rowsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
    marginTop: spacing(1),
    width: '100%',
  },
}));

function ListaAgendamentos({
  hasMore = false,
  hasFiltersApplied = false,
  onLoadNext,
  onSuccess,
}: ListaAgendamentosProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedActionAgendamentoId, setSelectedActionAgendamentoId] =
    useState<string>();

  const {
    idsDeAgendamentosSelecionados,
    agendamentosV2: agendamentos,
    loadingAgendamentosV2: loading,
  } = useAppSelector(agendamentosSelector);

  const handleUpdateSelecaoAgendamentos = useCallback(
    (itens: string[]) => {
      dispatch(setIdsAgendamentosSelecionados(itens));
    },
    [dispatch],
  );

  const handleCheckItem = useCallback(
    (id: string) => {
      if (idsDeAgendamentosSelecionados.includes(id)) {
        handleUpdateSelecaoAgendamentos(
          idsDeAgendamentosSelecionados.filter(item => item !== id),
        );
      } else {
        handleUpdateSelecaoAgendamentos([...idsDeAgendamentosSelecionados, id]);
      }
    },
    [handleUpdateSelecaoAgendamentos, idsDeAgendamentosSelecionados],
  );

  const handleCheckAll = useCallback(() => {
    if (idsDeAgendamentosSelecionados.length === agendamentos.length) {
      handleUpdateSelecaoAgendamentos([]);
    } else {
      handleUpdateSelecaoAgendamentos(agendamentos.map(item => item.id));
    }
  }, [
    idsDeAgendamentosSelecionados.length,
    agendamentos,
    handleUpdateSelecaoAgendamentos,
  ]);

  const renderAgendamentos = agendamentos.map(item => (
    <AgendamentoListItem
      key={item.id}
      item={item}
      selected={selectedActionAgendamentoId === item.id}
      onToggleActionSelection={() =>
        setSelectedActionAgendamentoId(currentId =>
          currentId === item.id ? undefined : item.id,
        )
      }
      itemChecked={idsDeAgendamentosSelecionados.includes(item.id)}
      onCheckItem={handleCheckItem}
      onSuccess={onSuccess}
    />
  ));

  const handleCancelInBatch = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    confirm({
      title: 'Cancelar agendamentos',
      description: 'Deseja realmente cancelar estes agendamentos?',
      cancellationText: 'Não',
      confirmationText: 'Sim',
      dialogProps: { maxWidth: 'xs', classes: { paper: classes.paper } },
    })
      .then(async () => {
        await dispatch(
          cancelarAgendamentosEmLote(idsDeAgendamentosSelecionados),
        )
          .unwrap()
          .then(() => {
            enqueueSnackbar(
              'Os agendamentos foram cancelados e não estão mais disponíveis para você.',
            );

            dispatch(resetIdsAgendamentosSelecionados());

            if (onSuccess) onSuccess();
          })
          .catch(error => {
            if (error.response) enqueueSnackbar(error.response.data.message);
          });
      })
      .catch(() => undefined);
  };

  const handleResetSelecaoAgendamentos = useCallback(() => {
    dispatch(resetIdsAgendamentosSelecionados());
  }, [dispatch]);

  const allItensChecked = useMemo(
    () =>
      agendamentos.length > 0 &&
      idsDeAgendamentosSelecionados.length === agendamentos.length,
    [idsDeAgendamentosSelecionados.length, agendamentos.length],
  );

  return (
    <>
      <Collapse in={idsDeAgendamentosSelecionados.length > 0} timeout={50}>
        <Box display="flex" alignItems="center" gridGap={40}>
          <Box paddingLeft={1} display="flex" alignItems="center" gridGap={2}>
            <Checkbox
              size="small"
              color="primary"
              checked={allItensChecked}
              onChange={handleCheckAll}
              id="check-all"
            />
            <Typography component="label" variant="body2" htmlFor="check-all">
              Selecionar todos
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gridGap={16}>
            <Typography variant="body2" color="textSecondary">
              {idsDeAgendamentosSelecionados.length}{' '}
              {idsDeAgendamentosSelecionados.length === 1
                ? 'item selecionado'
                : 'itens selecionados'}
            </Typography>

            <Button
              variant="text"
              style={{ fontWeight: 400 }}
              onClick={handleResetSelecaoAgendamentos}
            >
              Limpar seleção
            </Button>

            <Button
              startIcon={<Delete />}
              variant="text"
              color="secondary"
              onClick={handleCancelInBatch}
            >
              Cancelar agendamentos
            </Button>
          </Box>
        </Box>
      </Collapse>

      <List id="listAgendamentos" className={classes.list}>
        <InfiniteScroll
          data={agendamentos}
          next={onLoadNext}
          hasMore={hasMore}
          loading={loading}
          scrollableTarget="listAgendamentos"
          empty={
            hasFiltersApplied
              ? {
                  title: 'Tem certeza que digitou certo !?',
                  description: `Não encontramos ninguém com o filtro aplicado`,
                }
              : {
                  title: 'Ainda não há uma lista de agendamentos!',
                  description:
                    '"Adicione seus novos agendamentos e eles aparecerão aqui"',
                }
          }
        >
          <Box className={classes.rowsWrapper}>{renderAgendamentos}</Box>
        </InfiniteScroll>
      </List>
    </>
  );
}

export default ListaAgendamentos;
